/**
 * Step 3 do NewTenantWizard — Revisão (TASK-2435).
 *
 * Mostra um resumo dos recursos AWS que serão criados (best-effort) e expõe
 * o botão "Iniciar provisionamento" que dispara o POST /v1/admin/tenants/provision.
 */

import {
  Stack,
  Card,
  Text,
  Group,
  Badge,
  List,
  Divider,
  Button,
  Alert,
  ThemeIcon,
} from '@mantine/core'
import {
  IconRocket,
  IconClock,
  IconCheck,
  IconBuilding,
  IconAlertTriangle,
} from '@tabler/icons-react'
import {
  computeEstimatedSteps,
  countEnvTuples,
  estimateDuration,
  type TenantSpec,
} from '@/api/awsOrgProvisioning'

interface WizardStepRevisaoProps {
  spec: TenantSpec
  onConfirm: () => void
  loading?: boolean
}

export function WizardStepRevisao({
  spec,
  onConfirm,
  loading = false,
}: WizardStepRevisaoProps) {
  const totalSteps = computeEstimatedSteps(spec)
  const tuples = countEnvTuples(spec)
  const duration = estimateDuration(spec)

  return (
    <Stack gap="md" mt="md">
      <Alert
        variant="light"
        color="yellow"
        icon={<IconAlertTriangle size={18} />}
        title="Operação irreversível"
      >
        <Text size="sm">
          Confira os dados abaixo. Ao iniciar, o ConsoleAI vai criar contas AWS,
          IAM users e secrets. <b>O processo não pode ser desfeito automaticamente.</b>
        </Text>
      </Alert>

      <Card withBorder p="md" radius="sm">
        <Stack gap="xs">
          <Group>
            <ThemeIcon size={32} radius="md" variant="light" color="teal">
              <IconBuilding size={18} />
            </ThemeIcon>
            <div>
              <Text size="lg" fw={600}>
                {spec.project_name || '—'}
              </Text>
              <Text size="xs" c="dimmed">
                {spec.is_saas ? 'Tenant SaaS' : 'Tenant simples'}
                {spec.cost_center ? ` · CostCenter: ${spec.cost_center}` : ''}
              </Text>
            </div>
          </Group>

          <Divider my="xs" />

          {spec.direct_environments.length > 0 && (
            <Group gap={6}>
              <Text size="sm" fw={500}>
                Ambientes diretos:
              </Text>
              {spec.direct_environments.map((env) => (
                <Badge key={env} variant="light" color="blue">
                  {env}
                </Badge>
              ))}
            </Group>
          )}

          {spec.is_saas && spec.sub_tenants.length > 0 && (
            <>
              <Text size="sm" fw={500} mt="xs">
                Sub-tenants ({spec.sub_tenants.length}):
              </Text>
              <List spacing={4} size="sm" withPadding>
                {spec.sub_tenants.map((st, idx) => (
                  <List.Item key={idx}>
                    <Group gap={6}>
                      <Text fw={500} size="sm">
                        {st.name || '(sem nome)'}
                      </Text>
                      {st.environments.map((env) => (
                        <Badge
                          key={env}
                          size="sm"
                          variant="outline"
                          color="grape"
                        >
                          {env}
                        </Badge>
                      ))}
                    </Group>
                  </List.Item>
                ))}
              </List>
            </>
          )}
        </Stack>
      </Card>

      <Card withBorder p="md" radius="sm">
        <Text size="sm" fw={500} mb="xs">
          Recursos AWS que serão criados
        </Text>
        <List spacing={4} size="sm" icon={
          <ThemeIcon size={16} radius="xl" variant="light" color="teal">
            <IconCheck size={10} />
          </ThemeIcon>
        }>
          <List.Item>
            1 OU <code>{spec.project_name || '<projeto>'}</code> sob{' '}
            <i>Amazonas Inovare</i>
          </List.Item>
          {spec.direct_environments.length > 0 && (
            <List.Item>
              {spec.direct_environments.length} sub-OU(s) de ambiente:{' '}
              {spec.direct_environments.join(', ')}
            </List.Item>
          )}
          {spec.is_saas && (
            <List.Item>
              1 sub-OU <i>SaaS</i> + {spec.sub_tenants.length} sub-tenant OU(s)
            </List.Item>
          )}
          {tuples > 0 && (
            <>
              <List.Item>
                {tuples} conta(s) AWS (uma por par tenant×ambiente)
              </List.Item>
              <List.Item>
                {tuples} email(s) de root + verificação SES
              </List.Item>
              <List.Item>
                {tuples} IAM user(s) + {tuples} secret(s) no Secrets Manager
              </List.Item>
              <List.Item>Tags padrão (Project, Environment, CostCenter)</List.Item>
            </>
          )}
        </List>
      </Card>

      <Group justify="space-between">
        <Group gap="xs">
          <ThemeIcon size={28} radius="md" variant="light" color="gray">
            <IconClock size={16} />
          </ThemeIcon>
          <div>
            <Text size="xs" c="dimmed">
              Duração estimada
            </Text>
            <Text size="sm" fw={500}>
              {duration}
            </Text>
          </div>
        </Group>
        <Group gap="xs">
          <ThemeIcon size={28} radius="md" variant="light" color="gray">
            <IconRocket size={16} />
          </ThemeIcon>
          <div>
            <Text size="xs" c="dimmed">
              Passos
            </Text>
            <Text size="sm" fw={500}>
              ~{totalSteps}
            </Text>
          </div>
        </Group>
      </Group>

      <Button
        size="md"
        color="teal"
        leftSection={<IconRocket size={18} />}
        onClick={onConfirm}
        loading={loading}
        data-testid="wizard-confirm-provision"
      >
        Iniciar provisionamento
      </Button>
    </Stack>
  )
}
